import axios from 'axios';
import { API_HOST } from './csqConstants';
import { QUALITY_SERVICE_API_URL } from '../../config';
import oktaAuth from '../../auth/oktaAuth';

/*
 * Minimal browser-side client for the ContentSquare web API
 * (webapi.contentsquare.com), mirroring sales-demo-env-setup's
 * `src/lib/cs-api-client.js`.
 *
 * Wraps bearer auth, JSON encoding/decoding, and unwrapping of the standard
 * `{ payload, success }` envelope the API returns.
 *
 * webapi.contentsquare.com doesn't send CORS headers allowing the
 * quality-hub origin, so requests can't be made directly from the browser in
 * any environment. Instead, every request is routed through Quality
 * Service's `/hub/csq-proxy` passthrough (see app-quality-dashboard's
 * `src/hub/csq-proxy/csq-proxy.controller.ts`), which forwards it
 * server-to-server, where CORS (a browser-only mechanism) doesn't apply.
 */

export class CsqApiClient {
  /**
   * @param {object} options
   * @param {string} options.token - Bearer token (without the "Bearer " prefix).
   * @param {string} [options.host] - API host, defaults to webapi.contentsquare.com.
   * @param {boolean} [options.debug] - When true, logs each request/response to the console.
   */
  constructor({ token, host = API_HOST, debug = false }) {
    if (!token) {
      throw new Error('CsqApiClient requires a token.');
    }
    this.token = token;
    this.host = host;
    this.debug = Boolean(debug);
    // Dedicated axios instance (no interceptors) — the Hub auth header is
    // added explicitly per-request below instead, since this client also
    // needs to send the separate X-Csq-Authorization header.
    this.client = axios.create({
      baseURL: `${QUALITY_SERVICE_API_URL}/hub/csq-proxy/webapi`,
      validateStatus: () => true,
    });
  }

  _log(...parts) {
    if (this.debug) {
      // eslint-disable-next-line no-console
      console.log('[csq-api]', ...parts);
    }
  }

  async request(method, path, body) {
    const oktaToken = await oktaAuth.getOrRenewAccessToken();
    const headers = {
      Accept: 'application/json, text/plain, */*',
      'X-Csq-Authorization': `Bearer ${this.token}`,
    };
    if (oktaToken) {
      headers['Authorization'] = `Bearer ${oktaToken}`;
    }
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    this._log(`--> ${method} https://${this.host}${path}`, body !== undefined ? body : '');

    let res;
    try {
      res = await this.client.request({ method, url: path, data: body, headers });
    } catch (err) {
      throw new Error(
        `Request to ${this.host}${path} (via the Quality Service proxy) failed with no HTTP response. ` +
          `Original error: ${err.message}`,
      );
    }

    this._log(`<-- ${res.status} ${method} ${path}`, res.data);

    const parsed = res.data && typeof res.data === 'object' ? res.data : {};
    const ok = res.status >= 200 && res.status < 300;
    if (ok && parsed.success !== false) {
      return parsed.payload !== undefined ? parsed.payload : parsed;
    }
    throw new Error(`API error (HTTP ${res.status}) for ${method} ${path}: ${JSON.stringify(parsed)}`);
  }

  get(path) {
    return this.request('GET', path);
  }

  post(path, body) {
    return this.request('POST', path, body);
  }

  put(path, body) {
    return this.request('PUT', path, body);
  }

  patch(path, body) {
    return this.request('PATCH', path, body);
  }

  del(path, body) {
    return this.request('DELETE', path, body);
  }
}
